import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, dayLabel, hhmm, minutes as minutesWord, rubles, useAction, useResource } from '@limuzin/kit';
import type { ConfigView, PassengerRideView, TownTariffView } from '@limuzin/passenger';
import { cachedOrderPhone, getConfig, getTariff, order } from '../api';
import { OrderName } from '../ids';
import { askToNotify } from '../notify';
import { useActiveRide, useSession } from '../session';
import { Button, ErrorNote, GrowableBox, Note, Panel, Sheet, StopDot, TitledScreen, useFits } from '../widgets';
import { Icon } from '../material';
import { InstallCoach } from '../install';
import { PlacePicker } from './place-picker';
import { bandOffset, scrollFor, standingAt, typeAt } from './roller';
import {
  type Draft,
  type When,
  addStop,
  clearRow,
  isComplete,
  reorderRoute,
  scheduledFor,
  toStops,
  useDraft,
} from './draft';

/**
 * «Новый заказ» — where to, and when to send the car.
 *
 * What is deliberately absent is a price. The fare is time on a тариф and nobody
 * knows how long a trip takes until it is over, so the card states the rates and
 * refuses to guess a total (ADR 0002).
 *
 * On a wide screen «Когда подать машину» and «Тариф» sit side by side, which is the
 * one place the desktop layout differs from the phone: the route card needs the full
 * width because its rows are addresses, and these two do not.
 */
export function BookingScreen() {
  const { api, me, reload } = useSession();
  const busy = useActiveRide() !== null;
  const { draft, set, sent } = useDraft();
  // The row being edited, counted the way the route is drawn: 0 is «Откуда».
  const [editing, setEditing] = useState<number | null>(null);
  const [tariffOpen, setTariffOpen] = useState(false);

  const townId = me?.passenger?.townId ?? null;
  const tariff = useResource<TownTariffView>(
    () => getTariff(api, townId!),
    [api, townId],
    { enabled: Boolean(townId) },
  );
  const config = useResource<ConfigView>(() => getConfig(api, townId), [api, townId]);
  // The number as the server last gave it, on this visit or an earlier one: an ask
  // that failed for want of a connection is when ordering by phone is wanted most.
  const phone = config.data?.orderPhone ?? cachedOrderPhone();

  // A ride is already running, so «Сейчас» is not on offer — the server refuses a
  // second one, and a button that fails is worse than a button that explains.
  const when: When = busy && draft.when === 'now' ? 'today' : draft.when;

  const complete = isComplete(draft);

  // The предзаказ just placed, while «Поездка запланирована» is up for it.
  const [placed, setPlaced] = useState<PassengerRideView | null>(null);
  const navigate = useNavigate();

  // The id the order is sent under (ADR 0030): the same while the order is, so a tap
  // again after a lost answer is that order and not a second car.
  const [name] = useState(() => new OrderName());
  const send = useAction(async () => {
    if (!townId || !complete) return;
    // Somebody who has just ordered a car is somebody who wants to hear from its
    // driver, and this press is when a browser will ask them whether they may.
    askToNotify();
    const at = scheduledFor({ ...draft, when });
    const body = {
      townId,
      stops: toStops(draft),
      ...(at ? { scheduledFor: at.toISOString() } : {}),
      ...(draft.comment.trim() ? { comment: draft.comment.trim() } : {}),
    };
    const id = name.idFor(body);
    const ride = await order(api, { id, ...body }).catch((problem: unknown) => {
      // No answer is not the same as no order. The list is asked behind the error
      // rather than before it — on a link that has stopped answering that ask waits
      // as long again — and a «Сейчас» order that did arrive takes the tab by itself.
      // Pressed again, the order goes under the same name and is answered with the first.
      if (problem instanceof ApiError && problem.code === 'offline') void reload();
      throw problem;
    });
    name.done();
    adopt(ride);
    // A «Сейчас» order keeps its draft, for the passenger who comes back to this form
    // from «Ищем машину» — see `aboard`.
    if (ride.scheduledFor) {
      set({ from: '', stops: [''], comment: '', when: 'now' });
      setPlaced(ride);
    } else sent(ride.id);
  });
  const { adoptRide: adopt } = useSession();

  const label =
    when === 'now'
      ? 'Заказать'
      : `Заказать на ${when === 'today' ? 'сегодня' : 'завтра'}, ${two(draft.hour)}:${two(draft.minute)}`;

  return (
    <TitledScreen
      title="Новый заказ"
      subtitle="Куда поедем и когда подать машину"
      trailing={phone && (
        <a
          className="icon-btn booking-call"
          href={`tel:${phone}`}
          aria-label="Заказать по телефону"
          title="Заказать по телефону"
        >
          <Icon name="phone_in_talk_rounded" size={27} />
        </a>
      )}
    >
      <div className="column screen-list">
        {/* Only here, where there is nothing more urgent on the screen: the first
            thing a passenger opens. */}
        <InstallCoach />
        <div className="stack">
          <RouteCard
            draft={draft}
            onEdit={setEditing}
            onClear={(index) => set(clearRow(draft, index))}
            onRemove={(index) =>
              set({ stops: draft.stops.filter((_, i) => i !== index) })
            }
            onAdd={() => set(addStop(draft))}
            onReorder={(from, to) => set(reorderRoute(draft, from, to))}
          />

          {/* «Комментарий водителю». Two rows, and a corner to pull when two are not
              enough — what goes in here is подъезд, домофон and how much luggage
              there is, which is more than two rows' worth as often as not. */}
          <Panel radius={18} style={{ padding: '14px 16px' }}>
            <GrowableBox
              placeholder="Комментарий водителю — по желанию"
              value={draft.comment}
              onChange={(event) => set({ comment: event.target.value })}
            />
          </Panel>

          {/* Side by side on a wide window only: the route needs the full width
              because its rows are addresses, and these two do not. */}
          <div className="pair">
            <WhenCard
              draft={draft}
              when={when}
              busy={busy}
              preorderKopeks={tariff.data?.preorderKopeks ?? 0}
              set={set}
            />
            <TariffCard
              tariff={tariff.data}
              open={tariffOpen}
              onToggle={() => setTariffOpen((on) => !on)}
            />
          </div>
        </div>

        <ErrorNote error={send.error} />

        <div style={{ marginTop: 16 }}>
          <Button busy={send.pending} disabled={!complete} onClick={() => void send.run()}>
            {label}
          </Button>
        </div>
        {/* A grey button says «not yet» and nothing about why. The one thing it
            waits for is the pickup, so it says so under itself, and the line goes
            the moment there is one. */}
        {!complete && <p className="order-waits">Укажите, откуда вас забрать</p>}
      </div>

      {placed && (
        <PlacedSheet
          ride={placed}
          onClose={() => {
            setPlaced(null);
            navigate('/orders', { state: { focus: placed.id } });
          }}
        />
      )}

      {editing !== null && (
        <PlacePicker
          title={
            editing === 0
              ? 'Откуда едем?'
              : editing === draft.stops.length
                ? 'Куда едем?'
                : 'Остановка'
          }
          initial={editing === 0 ? draft.from : (draft.stops[editing - 1] ?? '')}
          onClose={() => setEditing(null)}
          onPick={(address) => {
            if (editing === 0) set({ from: address });
            else {
              set({
                stops: draft.stops.map((s, i) => (i === editing - 1 ? address : s)),
              });
            }
            setEditing(null);
          }}
        />
      )}
    </TitledScreen>
  );
}

const two = (n: number) => String(n).padStart(2, '0');

/**
 * «Поездка запланирована». The form a предзаказ leaves behind is empty, and an empty
 * form after a press reads as an order that did not go — so the passenger is told it
 * went, and for when, and is then taken to the card it became, however the sheet is
 * closed. It says what the card will show rather than promising a message: a browser
 * that refused notifications is told nothing until it is next opened. A port of
 * `_placed` in `booking_screen.dart`.
 */
function PlacedSheet({ ride, onClose }: { ride: PassengerRideView; onClose: () => void }) {
  const at = new Date(ride.scheduledFor!);
  return (
    <Sheet title="Поездка запланирована" onClose={onClose}>
      <div className="placed-when">
        <Icon name="check_circle" size={22} />
        <span>
          {dayLabel(at)} в {hhmm(at)}
        </span>
      </div>
      <p className="body placed-next">
        Сейчас заказ ожидает водителя. Когда водитель его примет, в карточке заказа
        появятся его имя и машина.
      </p>
      <Button size="card" onClick={onClose}>
        К запланированным поездкам
      </Button>
    </Sheet>
  );
}

/**
 * The route card. Every row opens the picker on a click and moves on a long press —
 * the pickup among them, so an address that went into the wrong row is dragged into
 * the right one rather than typed twice. Whatever ends up at the top is «Откуда»,
 * whatever ends up at the bottom is «Куда».
 *
 * Pointer events rather than HTML's own drag and drop, which does not fire for a
 * touch at all — and a touch is what this app is mostly held in.
 */
function RouteCard({
  draft,
  onEdit,
  onClear,
  onRemove,
  onAdd,
  onReorder,
}: {
  draft: Draft;
  onEdit: (index: number) => void;
  onClear: (index: number) => void;
  onRemove: (index: number) => void;
  onAdd: () => void;
  onReorder: (from: number, to: number) => void;
}) {
  const route = [draft.from, ...draft.stops];
  const card = useRef<HTMLDivElement>(null);
  const rows = useRef<(HTMLDivElement | null)[]>([]);
  const armed = useRef(0);
  const start = useRef(0);
  // Whether the press turned into a drag, so that letting go of a row does not also
  // open the picker on it.
  const moved = useRef(false);
  // Set the instant a row is picked up, not a render later, so the very first move
  // of the finger is already kept from scrolling the page.
  const holding = useRef(false);
  const [drag, setDrag] = useState<{ index: number; shift: number } | null>(null);

  // Rows to slide from where they were drawn into the place a swap or a drop just
  // gave them, once the render that gives it is on the page.
  const glides = useRef<{ index: number; from: Keyframe }[]>([]);

  const release = () => {
    window.clearTimeout(armed.current);
    holding.current = false;
    const row = drag ? rows.current[drag.index] : null;
    if (drag && row) {
      // Let go anywhere over its place, the row settles into it rather than
      // jumping, and puts its shadow down on the way.
      const look = getComputedStyle(row);
      glides.current.push({
        index: drag.index,
        from: {
          transform: `translateY(${drag.shift}px)`,
          boxShadow: look.boxShadow,
          backgroundColor: look.backgroundColor,
          zIndex: 2,
        },
      });
    }
    setDrag(null);
  };

  const press = (index: number, event: React.PointerEvent<HTMLDivElement>) => {
    const { pointerId, clientY } = event;
    const row = rows.current[index];
    start.current = clientY;
    moved.current = false;
    window.clearTimeout(armed.current);
    armed.current = window.setTimeout(() => {
      moved.current = true;
      holding.current = true;
      row?.setPointerCapture(pointerId);
      setDrag({ index, shift: 0 });
    }, 400);
  };

  const follow = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!drag) {
      // A press that moved before it armed was the page being scrolled under the
      // finger, not a row being picked up. The row lets go of it.
      if (Math.abs(event.clientY - start.current) > 8) window.clearTimeout(armed.current);
      return;
    }

    const live = rows.current
      .slice(0, route.length)
      .filter((row): row is HTMLDivElement => row !== null);
    if (live.length !== route.length) return;
    // Where each row is drawn, and where it stands in the layout — the same but for
    // the dragged row's shift and a slide still under way.
    const drawn = live.map((row) => row.getBoundingClientRect().top);
    const height = live.map((row) => row.getBoundingClientRect().height);
    const stands = live.map((row, i) => {
      const transform = getComputedStyle(row).transform;
      return drawn[i]! - (transform === 'none' ? 0 : new DOMMatrixReadOnly(transform).m42);
    });
    const middle = (i: number) => stands[i]! + height[i]! / 2;
    const y = event.clientY;

    // The row rides under the finger, and takes a neighbour's place once the finger
    // is past that neighbour's middle. The row itself is left out of the test — it
    // is always under the finger, and would otherwise keep its own place.
    let target = drag.index;
    while (target + 1 < live.length && y > middle(target + 1)) target++;
    while (target > 0 && y < middle(target - 1)) target--;

    if (target !== drag.index) {
      // Rows are keyed by place, so every row between the two places now shows
      // the address of its neighbour on the side the dragged row came from. Each
      // slides in from where that address was drawn.
      const step = target > drag.index ? 1 : -1;
      for (let i = drag.index; i !== target; i += step) {
        glides.current.push({
          index: i,
          from: { transform: `translateY(${drawn[i + step]! - stands[i]!}px)` },
        });
      }
      onReorder(drag.index, target);
      setDrag({ index: target, shift: y - middle(target) });
      return;
    }
    setDrag({ index: drag.index, shift: y - middle(drag.index) });
  };

  useLayoutEffect(() => {
    // The dragged row follows its inline shift, which a slide it was in the middle
    // of would override.
    if (drag) for (const running of rows.current[drag.index]?.getAnimations() ?? []) running.cancel();

    const pending = glides.current;
    glides.current = [];
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    for (const { index, from } of pending) {
      const row = rows.current[index];
      if (!row) continue;
      for (const running of row.getAnimations()) running.cancel();
      row.animate([from, { transform: 'translateY(0)', zIndex: from.zIndex ?? 'auto' }], {
        duration: 200,
        easing: 'cubic-bezier(0.22, 0.9, 0.26, 1)',
      });
    }
  });

  // A touch that is dragging a row is not scrolling the page. The listener sits on
  // the card for as long as it is drawn: the touch stays with the element it started
  // on, which after a swap is no longer the row at the dragged index, and a
  // listener added once the drag has rendered misses the moves before it.
  // React's own listeners are passive and cannot do this.
  useEffect(() => {
    const node = card.current;
    if (!node) return;
    const hold = (event: TouchEvent) => {
      if (holding.current) event.preventDefault();
    };
    node.addEventListener('touchmove', hold, { passive: false });
    return () => node.removeEventListener('touchmove', hold);
  }, []);

  return (
    <div ref={card} className="panel r18" style={{ padding: 6 }}>
      {route.map((value, index) => {
        const pickup = index === 0;
        const last = index === route.length - 1;
        const moving = drag?.index === index;
        return (
          <div key={index}>
            {index > 0 && <div className="hairline" style={{ marginLeft: 35 }} />}
            <div
              ref={(node) => {
                rows.current[index] = node;
              }}
              className={`stop-row${moving ? ' moving' : ''}`}
              style={
                moving
                  ? { transform: `translateY(${drag.shift}px)` }
                  : undefined
              }
              onPointerDown={(event) => press(index, event)}
              onPointerMove={follow}
              onPointerUp={release}
              onPointerCancel={release}
              // A long press on a touch screen otherwise offers to select the text
              // or opens the browser's own menu over the row being dragged.
              onContextMenu={(event) => event.preventDefault()}
            >
              <StopRow
                label={pickup ? 'Откуда' : last ? 'Куда' : `Остановка ${index}`}
                value={value}
                // «Куда» may stay empty — the button lights up on «Откуда» alone —
                // but the row doesn't say so, or fewer would fill it.
                placeholder="Место или адрес"
                dot={pickup ? 'start' : last ? 'end' : 'middle'}
                onClick={() => !moved.current && onEdit(index)}
                onClear={() => onClear(index)}
                // The pickup row cannot be removed — the car has to be sent
                // somewhere — and neither can the last remaining stop row.
                onRemove={
                  !pickup && draft.stops.length > 1
                    ? () => onRemove(index - 1)
                    : undefined
                }
              />
            </div>
          </div>
        );
      })}
      <div className="hairline" style={{ marginLeft: 35 }} />
      <button type="button" className="add-stop" onClick={onAdd}>
        <span className="add-stop-plus">+</span>
        <span>Добавить остановку</span>
      </button>
    </div>
  );
}

function StopRow({
  label,
  value,
  placeholder,
  dot,
  onClick,
  onClear,
  onRemove,
}: {
  label: string;
  value: string;
  placeholder: string;
  dot: 'start' | 'middle' | 'end';
  onClick: () => void;
  onClear: () => void;
  onRemove?: () => void;
}) {
  const filled = value.trim().length > 0;
  // A filled row's ✕ empties it; an empty one's takes the row away, where the row
  // can go. A stop goes in two taps, then, and an address is never lost along with
  // its row by a tap meant for the address.
  const onClose = filled ? onClear : onRemove;
  const closeLabel = filled ? 'Очистить адрес' : 'Убрать остановку';
  return (
    <>
      <button type="button" className="stop-hit" onClick={onClick}>
        <StopDot kind={dot} />
        <span className="grow">
          <span className="label" style={{ display: 'block' }}>
            {label}
          </span>
          <span className={filled ? 'stop-value ellipsis' : 'stop-value ellipsis empty'}>
            {filled ? value : placeholder}
          </span>
        </span>
      </button>
      {/* The ✕ stays the size the design draws it; the square around it is a
          thumb's, so clearing a row is not a second try after landing on the row.
          The row itself goes under a bin, the size the app's other bins are, so
          the second tap does not look like the first one again. */}
      {onClose && (
        <button
          type="button"
          className="stop-x"
          onClick={onClose}
          aria-label={closeLabel}
          title={closeLabel}
        >
          {filled ? <Icon name="close" size={15} /> : <Icon name="delete_outline" size={18} />}
        </button>
      )}
    </>
  );
}

/** «Когда подать машину» — now, or a time today or tomorrow. */
function WhenCard({
  draft,
  when,
  busy,
  preorderKopeks,
  set,
}: {
  draft: Draft;
  when: When;
  busy: boolean;
  /** «Надбавка за предзаказ» in the passenger's town; 0 where it charges none. */
  preorderKopeks: number;
  set: (patch: Partial<Draft>) => void;
}) {
  const options: { value: When; label: string }[] = [
    { value: 'now', label: 'Сейчас' },
    { value: 'today', label: 'Сегодня' },
    { value: 'tomorrow', label: 'Завтра' },
  ];

  return (
    <Panel radius={18} style={{ padding: 16 }}>
      <h2 className="heading">Когда подать машину</h2>

      {busy && (
        <Note style={{ marginTop: 12 }}>
          У вас идёт поездка. Поездку «сейчас» можно заказать после её завершения —
          пока доступен предзаказ на другое время.
        </Note>
      )}

      <div className="day-chips" style={{ marginTop: 14 }}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={when === option.value ? 'on' : ''}
            disabled={busy && option.value === 'now'}
            onClick={() => set({ when: option.value })}
          >
            {option.label}
          </button>
        ))}
      </div>

      {when !== 'now' && (
        <>
          <div className="row" style={{ justifyContent: 'center', marginTop: 16, gap: 0 }}>
            <Roller
              count={24}
              index={draft.hour}
              label={two}
              onChange={(hour) => set({ hour })}
            />
            <span className="numeral roller-colon">:</span>
            {/* Minutes move in fives: nobody orders a taxi for 19:37. */}
            <Roller
              count={12}
              index={draft.minute / 5}
              label={(i) => two(i * 5)}
              onChange={(i) => set({ minute: i * 5 })}
            />
          </div>
          <p className="body" style={{ textAlign: 'center', margin: '12px 0 0', fontSize: 14 }}>
            Машина приедет в выбранное время ±5 минут
          </p>
          {/* Said where the time is picked, not only on the folded «Тариф» card:
              this is the choice that costs it. */}
          {preorderKopeks > 0 && (
            <p className="body" style={{ textAlign: 'center', margin: '4px 0 0', fontSize: 14 }}>
              Надбавка за предзаказ — {rubles(preorderKopeks)}
            </p>
          )}
        </>
      )}
    </Panel>
  );
}

/**
 * One column of the wheel — the hours, or the minutes in fives.
 *
 * The browser does the rolling: a scroll container that snaps to whole numbers, with
 * the momentum a thumb expects. What the column does for itself is say which number
 * is chosen, and it says it twice over — the band inks what is inside it, and the
 * numbers shrink as they leave it.
 *
 * Both follow where the column is standing, to the fraction, rather than which
 * number is nearest. Keyed to the nearest number instead, two of them would swap
 * sizes in a single frame at the halfway point of every scroll, and the column would
 * appear to jump from one value to the next rather than roll between them. That is
 * also why the type is written straight onto the nodes: it follows the scroll, which
 * is faster than React has any business being.
 */
function Roller({
  count,
  index,
  label,
  onChange,
}: {
  count: number;
  index: number;
  label: (index: number) => string;
  onChange: (index: number) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const frame = useRef(0);
  const sized = useRef<number[]>([]);
  // The last number this column reported. The screen hands it back as a new
  // `index`, and an echo of our own scrolling is not a reason to jump anywhere.
  const reported = useRef<number | null>(null);

  const paint = useCallback(() => {
    const el = box.current;
    if (!el) return;
    const standing = standingAt(el.scrollTop);

    for (let i = 0; i < count; i += 1) {
      const node = el.children[i] as HTMLElement | undefined;
      if (!node) continue;
      const gap = Math.abs(i - standing);

      // Manrope is one variable file, so a weight between two of the design's is a
      // real weight rather than a synthetic one.
      const { size, weight } = typeAt(gap);
      if (sized.current[i] !== size) {
        sized.current[i] = size;
        node.style.fontSize = `${size}px`;
        node.style.fontVariationSettings = `"wght" ${weight}`;
        node.style.fontWeight = `${Math.round(weight)}`;
      }
      // The gradient that inks the band is nailed to the window the column rolls
      // behind, so each number carries the slice of it that it is under. The two
      // numbers' worth of nothing above the first one count towards where that is.
      if (gap <= 3) node.style.backgroundPositionY = `${bandOffset(i, el.scrollTop)}px`;
    }

    const nearest = Math.max(0, Math.min(count - 1, Math.round(standing)));
    if (nearest !== reported.current) {
      reported.current = nearest;
      onChange(nearest);
    }
  }, [count, onChange]);

  // Scroll events arrive faster than frames; one repaint a frame is enough.
  const roll = useCallback(() => {
    if (frame.current) return;
    frame.current = window.requestAnimationFrame(() => {
      frame.current = 0;
      paint();
    });
  }, [paint]);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.scrollTop = scrollFor(index);
    reported.current = index;
    paint();
    // Only on the way in: from there the column is the thumb's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => window.cancelAnimationFrame(frame.current), []);

  // A value that moved on its own — «Повторить», or the draft reset after an order.
  useEffect(() => {
    const el = box.current;
    if (!el || index === reported.current) return;
    reported.current = index;
    el.scrollTo({ top: scrollFor(index) });
  }, [index]);

  return (
    <div className="roller">
      <div className="roller-band" />
      <div className="roller-numbers" ref={box} onScroll={roll}>
        {Array.from({ length: count }, (_, i) => (
          <button
            key={i}
            type="button"
            className="roller-number"
            onClick={() =>
              box.current?.scrollTo({ top: scrollFor(i), behavior: 'smooth' })
            }
          >
            {label(i)}
          </button>
        ))}
      </div>
    </div>
  );
}

/** «Тариф» — the name the town charges under, the rates, and no total. */
function TariffCard({
  tariff,
  open,
  onToggle,
}: {
  tariff: TownTariffView | undefined;
  open: boolean;
  onToggle: () => void;
}) {
  // The whole card opens and shuts it, «Подробнее» included.
  return (
    <button type="button" className="panel r18 tariff" onClick={onToggle} aria-expanded={open}>
      <span className="row" style={{ alignItems: 'flex-start' }}>
        <span className="grow">
          <span className="label" style={{ display: 'block' }}>
            Тариф
          </span>
          {/* Whatever the town runs — «Базовый» in one, «Ночной» in the next.
              Nothing is shown until the server has said which. */}
          {tariff?.name && <span className="tariff-name">{tariff.name}</span>}
        </span>
        <span className="tariff-toggle">{open ? 'Скрыть' : 'Подробнее'}</span>
      </span>

      {open && tariff && <TariffRows tariff={tariff} />}
    </button>
  );
}

const FREE_WAITING = 'Ожидание при подаче 0\u00A0₽';
const PAID_WAITING = 'Ожидание далее и в пути';
const FREE_TRANSIT = 'Бесплатно в пути';

/**
 * The tariff's lines. «первые 3 мин» for «первые 3 минуты» where a row has no room for
 * the whole of it beside its label — a small phone, or a browser set to large text — and
 * then on every row, so the card does not write minutes two ways at once. Where that is
 * still not enough, «Ожид.» for «Ожидание», on both rows that start with it, for the same
 * reason. A port of the rows in `_TariffCard`.
 */
function TariffRows({ tariff }: { tariff: TownTariffView }) {
  const [space, whole, fits] = useFits();
  const waiting = Math.round(tariff.freeWaitingSeconds / 60);
  const transit = Math.round(tariff.freeTransitSeconds / 60);
  const first = (n: number) => (fits ? `первые ${minutesWord(n)}` : `первые ${n} мин`);
  const [, spelledOut, spelled] = useFits(space);
  const wait = (label: string) => (spelled ? label : label.replace('Ожидание', 'Ожид.'));
  const perMinute = `${rubles(tariff.waitingPerMinuteKopeks)} / мин`;
  return (
    <span className="tariff-rows" ref={space as RefObject<HTMLSpanElement>}>
      <TariffRow label="Подача" value={rubles(tariff.boardingKopeks)} />
      <TariffRow label={wait(FREE_WAITING)} value={first(waiting)} />
      <TariffRow label={wait(PAID_WAITING)} value={perMinute} />
      {/* A town whose tariff gives no free minutes says nothing about them. */}
      {tariff.freeTransitSeconds > 0 && <TariffRow label={FREE_TRANSIT} value={first(transit)} />}
      <TariffRow label="Минута в пути" value={rubles(tariff.perMinuteKopeks)} />
      {/* Nor one that charges a предзаказ what it charges «Сейчас». */}
      {tariff.preorderKopeks > 0 && (
        <TariffRow label="Надбавка за предзаказ" value={rubles(tariff.preorderKopeks)} />
      )}
      {/* The rows that can shorten, whole and unseen: as wide as the widest of them. */}
      <span className="measure" aria-hidden="true" ref={whole as RefObject<HTMLSpanElement>}>
        <TariffRow label={FREE_WAITING} value={`первые ${minutesWord(waiting)}`} />
        {tariff.freeTransitSeconds > 0 && (
          <TariffRow label={FREE_TRANSIT} value={`первые ${minutesWord(transit)}`} />
        )}
      </span>
      {/* The «Ожидание» rows spelled out, with the minutes as the card now writes them. */}
      <span className="measure" aria-hidden="true" ref={spelledOut as RefObject<HTMLSpanElement>}>
        <TariffRow label={FREE_WAITING} value={first(waiting)} />
        <TariffRow label={PAID_WAITING} value={perMinute} />
      </span>
      <span className="tariff-note">
        Итоговая сумма зависит от времени в пути и платного ожидания.
      </span>
    </span>
  );
}

function TariffRow({ label, value }: { label: string; value: string }) {
  return (
    <span className="tariff-row">
      <span className="grow">{label}</span>
      <b>{value}</b>
    </span>
  );
}
